import { useState, useEffect, useRef } from "react";
import { isActiveEmp } from "../lib/constants";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "../components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Loader2, PenLine, Bike } from "lucide-react";
import { api } from "../lib/api";
import { useLang } from "../i18n/LanguageContext";
import { SignaturePad } from "./SignaturePad";
import { VehicleInspectionForm } from "./VehicleInspectionForm";
import { isMotoText } from "./VehicleDamageDiagram";
import { isVehicleType, vehicleTypeLabel } from "../lib/vehicles";

const today = () => new Date().toISOString().slice(0, 10);

// Handover dialog shared by CustodyPanel and VehiclesPanel.
// When the asset is a vehicle, it asks about the previous holder's status.
export const AssignAssetDialog = ({ asset, onOpenChange, onSaved, employees }) => {
  const { t, dir } = useLang();
  const [form, setForm] = useState({ employee_id: "", received_at: today(), note: "" });
  const [prevReceived, setPrevReceived] = useState(null); // null | true | false
  const [prevPlate, setPrevPlate] = useState("");
  const [prevType, setPrevType] = useState("دراجة نارية");
  const [prevAssetId, setPrevAssetId] = useState("");
  const [vehicleOpts, setVehicleOpts] = useState([]);
  const [saving, setSaving] = useState(false);
  const sigRef = useRef(null);
  const inspRef = useRef(null);

  useEffect(() => {
    if (asset) {
      setForm({ employee_id: "", received_at: today(), note: "" });
      setPrevReceived(null); setPrevPlate(""); setPrevType("دراجة نارية"); setPrevAssetId("");
      if (isVehicleType(asset.type)) {
        api.custodyAssets().then((res) => {
          const list = Array.isArray(res) ? res : (res?.assets || []);
          // Only available vehicles (no current holder) to avoid transferring a bike held by another rider.
          setVehicleOpts(list.filter((v) => isVehicleType(v.type) && v.id !== asset.id
            && (!v.current || v.current.employee_id === "__admin__")));
        }).catch(() => setVehicleOpts([]));
      } else {
        setVehicleOpts([]);
      }
    }
  }, [asset]);

  const onPickPrevVehicle = (id) => {
    const v = vehicleOpts.find((x) => x.id === id);
    setPrevAssetId(id);
    setPrevPlate(v?.identifier || "");
    setPrevType(vehicleTypeLabel(v?.type) || "دراجة نارية");
  };

  const isVehicle = isVehicleType(asset?.type);
  const prevHolder = asset?.current && asset.current.employee_id !== "__admin__" ? asset.current : null;
  const showPrevPrompt = isVehicle && Boolean(prevHolder);

  const save = async () => {
    if (!form.employee_id) { toast.warning(t("dash.custody.toast.chooseEmp")); return; }
    if (showPrevPrompt && prevReceived === null) { toast.warning(t("dash.custody.assign.prevAnswerWarn")); return; }
    if (showPrevPrompt && prevReceived === true && !prevPlate.trim()) { toast.warning(t("dash.custody.assign.prevPlateWarn")); return; }
    setSaving(true);
    try {
      const payload = { ...form, signature_data: sigRef.current?.getSignature() || null };
      if (isVehicle && inspRef.current) {
        const insp = inspRef.current.getInspection();
        const photos = inspRef.current.getPhotos();
        if (insp) payload.inspection = insp;
        if (photos && photos.length) payload.inspection_photos = photos;
      }
      if (showPrevPrompt) {
        payload.prev_action = prevReceived ? "received_other" : "no_vehicle";
        if (prevReceived) { payload.prev_new_plate = prevPlate.trim(); payload.prev_new_vehicle_type = prevType; }
      }
      await api.assignAsset(asset.id, payload);
      toast.success(t("dash.custody.toast.assignDone"));
      onOpenChange(false);
      onSaved();
    } catch { toast.error(t("dash.custody.toast.assignFail")); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={Boolean(asset)} onOpenChange={(v) => !v && onOpenChange(false)}>
      <DialogContent className="sm:max-w-lg font-body max-h-[90vh] overflow-y-auto" dir={dir}>
        <DialogHeader className="text-start">
          <DialogTitle className="font-heading text-xl flex items-center gap-2">
            {isVehicle && <Bike className="h-5 w-5 text-blue-600" />}
            {t("dash.custody.assign.title")}
          </DialogTitle>
          <DialogDescription className="text-start">
            {asset?.type}{asset?.identifier ? ` · ${asset.identifier}` : ""}
            {asset?.current ? ` ${t("dash.custody.assign.currentHolder", { name: asset.current.employee_name })}` : ""}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-1">
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label>{t("dash.custody.assign.newReceiver")}</Label>
              <Select value={form.employee_id} onValueChange={(v) => setForm({ ...form, employee_id: v })}>
                <SelectTrigger data-testid="assign-employee-select"><SelectValue placeholder={t("dash.custody.form.chooseEmpPh")} /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__admin__">{t("dash.custody.form.admin")}</SelectItem>
                  {employees.filter(isActiveEmp).map((e) => <SelectItem key={e.id} value={e.id}>{e.name}{e.branch ? ` — ${e.branch}` : ""}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="assign-received">{t("dash.custody.form.receivedAt")}</Label>
              <Input id="assign-received" type="date" data-testid="assign-received-input" value={form.received_at}
                onChange={(e) => setForm({ ...form, received_at: e.target.value })} />
            </div>
          </div>

          {showPrevPrompt && (
            <div className="rounded-xl border border-amber-200 bg-amber-50/60 p-3 space-y-3" data-testid="prev-holder-prompt">
              <p className="text-sm font-semibold text-amber-800">
                {t("dash.custody.assign.prevQuestion", { name: prevHolder.employee_name })}
              </p>
              <div className="flex gap-2">
                <Button type="button" size="sm" variant={prevReceived === true ? "default" : "outline"}
                  data-testid="prev-received-yes"
                  className={prevReceived === true ? "bg-blue-600 hover:bg-blue-700" : ""}
                  onClick={() => setPrevReceived(true)}>{t("dash.custody.assign.prevYes")}</Button>
                <Button type="button" size="sm" variant={prevReceived === false ? "default" : "outline"}
                  data-testid="prev-received-no"
                  className={prevReceived === false ? "bg-amber-600 hover:bg-amber-700" : ""}
                  onClick={() => setPrevReceived(false)}>{t("dash.custody.assign.prevNo")}</Button>
              </div>
              {prevReceived === true && (
                <div className="space-y-1.5">
                  <Label>{t("dash.custody.assign.prevNewPlate")}</Label>
                  <Select value={prevAssetId} onValueChange={onPickPrevVehicle}>
                    <SelectTrigger data-testid="prev-new-plate-select"><SelectValue placeholder="اختر المركبة من القائمة" /></SelectTrigger>
                    <SelectContent position="item-aligned">
                      {vehicleOpts.length === 0 && (
                        <div className="px-3 py-2 text-xs text-slate-400">لا توجد مركبات أخرى مسجّلة</div>
                      )}
                      {vehicleOpts.map((v) => (
                        <SelectItem key={v.id} value={v.id} data-testid={`prev-vehicle-opt-${v.id}`}>
                          {v.identifier}{v.fuel_code ? ` — كود ${v.fuel_code}` : ""} · متاحة
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {prevPlate && (
                    <p className="text-[11px] text-amber-700" data-testid="prev-code-move-note">
                      سيُنقل رقم الكود المرتبط بهذه المركبة إلى {prevHolder.employee_name} تلقائيًا.
                    </p>
                  )}
                </div>
              )}
              {prevReceived === false && (
                <p className="text-xs text-amber-700" data-testid="prev-no-vehicle-note">
                  {t("dash.custody.assign.prevNoNote", { name: prevHolder.employee_name })}
                </p>
              )}
            </div>
          )}

          <div className="space-y-1.5">
            <Label>{t("dash.custody.assign.note")}</Label>
            <Input data-testid="assign-note-input" value={form.note}
              onChange={(e) => setForm({ ...form, note: e.target.value })} placeholder={t("dash.custody.assign.notePh")} />
          </div>
          <div className="space-y-1.5">
            <Label className="flex items-center gap-1.5"><PenLine className="h-3.5 w-3.5" /> {t("dash.custody.form.receiverSig")}</Label>
            <SignaturePad ref={sigRef} />
          </div>
          {isVehicle && <VehicleInspectionForm ref={inspRef} kind={isMotoText(`${asset?.type} ${asset?.vehicle_type || ""} ${asset?.kind || ""}`) ? "moto" : "car"} />}
        </div>
        <DialogFooter className="flex-row-reverse gap-2 sm:justify-start">
          <Button data-testid="confirm-assign-button" disabled={saving}
            className="bg-blue-600 hover:bg-blue-700 gap-1.5" onClick={save}>
            {saving && <Loader2 className="h-4 w-4 animate-spin" />} {t("dash.custody.assign.confirm")}
          </Button>
          <Button variant="outline" onClick={() => onOpenChange(false)}>{t("dash.custody.form.cancel")}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
